import * as Yup from "yup";
import {
  FieldMetadataForTypes as FieldMetadata,
  IssueCustomAttributeDefinition,
  IssueStandardFieldIds,
} from "@adsk/acc-issues-types";
import { getCustomFieldEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueCustomFields";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import {
  useGetSingleCustomFieldSchema,
  useIssueValidationSchemaObject,
} from "../../../../utils/validation/entitySchemas";
import { useWatcherPropName } from "../../../../utils/hooks/watchers/useWatcherPropName";

export const useIssueTypeChangeValidationSchema = ({
  requiredFieldsMetadata,
}: {
  requiredFieldsMetadata: FieldMetadata[];
}) => {
  const customFieldEntitiesMap = useSelector(getCustomFieldEntitiesMap);
  const getSingleCustomFieldSchema = useGetSingleCustomFieldSchema();
  const issueValidationSchemaObj = useIssueValidationSchemaObject();
  const standardFieldIds = requiredFieldsMetadata
    .filter(({ type }) => type === "standard")
    .map(({ id }) => id);
  const customFieldIds = requiredFieldsMetadata
    .filter(({ type }) => type === "custom")
    .map(({ id }) => id);

  const issueTypeChangeValidationSchemaObj: { [key: string]: Yup.Schema<any> } =
    {
      // we only need these so that the schemas in this form correctly
      // determine that every field is indeed required
      status: Yup.string(),
      issueTypeId: Yup.string(),
    };

  const watcherPropName = useWatcherPropName();

  for (const fieldId of standardFieldIds) {
    if (fieldId === IssueStandardFieldIds.Description) {
      issueTypeChangeValidationSchemaObj.description =
        issueValidationSchemaObj.description;
    } else if (fieldId === IssueStandardFieldIds.Assignee) {
      issueTypeChangeValidationSchemaObj.assignedTo =
        issueValidationSchemaObj.assignedTo;
    } else if (fieldId === IssueStandardFieldIds.Watchers) {
      issueTypeChangeValidationSchemaObj[watcherPropName] =
        issueValidationSchemaObj[watcherPropName];
    } else if (fieldId === IssueStandardFieldIds.Location) {
      issueTypeChangeValidationSchemaObj.locationId =
        issueValidationSchemaObj.locationId;
    } else if (fieldId === IssueStandardFieldIds.LocationDetails) {
      issueTypeChangeValidationSchemaObj.locationDetails =
        issueValidationSchemaObj.locationDetails;
    } else if (fieldId === IssueStandardFieldIds.DueDate) {
      issueTypeChangeValidationSchemaObj.dueDate =
        issueValidationSchemaObj.dueDate;
    } else if (fieldId === IssueStandardFieldIds.StartDate) {
      issueTypeChangeValidationSchemaObj.startDate =
        issueValidationSchemaObj.startDate;
    } else if (fieldId === IssueStandardFieldIds.RootCauseWithCategory) {
      issueTypeChangeValidationSchemaObj.rootCauseId =
        issueValidationSchemaObj.rootCauseId;
    }
  }

  const customFieldSchemaObj = Object.fromEntries(
    customFieldIds
      .map(attributeDefinitionId => {
        const attribute = customFieldEntitiesMap[
          attributeDefinitionId
        ] as IssueCustomAttributeDefinition;
        return [
          attributeDefinitionId,
          attribute &&
            getSingleCustomFieldSchema({ attribute, isRequired: true }),
        ];
      })
      .filter(([, validation]) => validation)
  );
  issueTypeChangeValidationSchemaObj.customAttributes =
    Yup.object().shape(customFieldSchemaObj);
  return Yup.object().shape(issueTypeChangeValidationSchemaObj);
};
